import React, { useState, useEffect } from "react";
import axios from "axios";
import { Table, Form, Container, Pagination, Modal, Button, Row, Col, ProgressBar as Progress } from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import DriverSelector from "../../components/driverSelector";
import WeekSelector from "../weekSelector";
import { AiOutlineEdit, AiOutlineDelete, AiOutlinePlus, AiOutlineMinus, AiOutlineTeam, AiOutlineUnorderedList } from "react-icons/ai";
import { useTranslation } from 'react-i18next';

const getPrevWeekNumber = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const firstMonday = new Date(currentYear, 0, 1);
    while (firstMonday.getDay() !== 1) firstMonday.setDate(firstMonday.getDate() + 1);
    const prevWeekDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const weekStart = new Date(firstMonday);
    let currentWeekNumber = 1;
    while (weekStart <= prevWeekDate) {
        weekStart.setDate(weekStart.getDate() + 7);
        currentWeekNumber++;
    }
    return `${currentYear}${String(currentWeekNumber - 1).padStart(2, "0")}`;
};

const ExtraFunds = () => {
    const { t } = useTranslation();
    const [funds, setFunds] = useState([]);
    const [allDebts, setAllDebts] = useState([]);
    const [filters, setFilters] = useState({ user_id: null });
    const [selectedWeek, setSelectedWeek] = useState(() => getPrevWeekNumber());
    const [editCell, setEditCell] = useState({ id: null, field: null });
    const [editedValue, setEditedValue] = useState("");
    const navigate = useNavigate();
    const [onlyCurrentWeek, setOnlyCurrentWeek] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [lastClickedIndex, setLastClickedIndex] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 40;
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const [skipBlur, setSkipBlur] = useState(false);
    const [showDriverDebtsModal, setShowDriverDebtsModal] = useState(false);
    const [driverDebtsModalDriverId, setDriverDebtsModalDriverId] = useState(null);
    const [createForAllOverlay, setCreateForAllOverlay] = useState(false);
    const [createForAllProgress, setCreateForAllProgress] = useState(0);
    const [createForAllCurrent, setCreateForAllCurrent] = useState(0);
    const [createForAllTotal, setCreateForAllTotal] = useState(0);
    const [createForAllCurrentDriverName, setCreateForAllCurrentDriverName] = useState("");

    useEffect(() => {
        fetchFunds();
    }, []);

    useEffect(() => {
        axios.get("/api/drivers-debts", { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } })
            .then((res) => setAllDebts(res.data || []))
            .catch((err) => console.error("Помилка завантаження боргів:", err));
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedWeek, filters.user_id]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                setSelectedRows([]);
                setLastClickedIndex(null);
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);


    const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

    const fetchFunds = async () => {
        try {
            const response = await axios.get("/api/extraFunds", authHeaders());
            const sortedFunds = (response.data || [])
                .sort((a, b) => {
                    const wa = String(a.week_number || "");
                    const wb = String(b.week_number || "");
                    if (wa !== wb) return wb.localeCompare(wa);
                    const na = `${a.lastName || ""} ${a.firstName || ""}`;
                    const nb = `${b.lastName || ""} ${b.firstName || ""}`;
                    return na.localeCompare(nb);
                })
                .slice(0, 500);
            setFunds(sortedFunds);
        } catch (error) {
            console.error("Error fetching extra funds", error);
        }
    };


    const handleDoubleClick = (id, field, currentValue) => {
        setEditCell({ id, field });
        setEditedValue(currentValue);
    };

    const handleRowClick = (e, index, id) => {
        if (e.shiftKey && lastClickedIndex !== null) {
            const [start, end] = [lastClickedIndex, index].sort((a, b) => a - b);
            const rangeIds = filteredFunds.slice(start, end + 1).map(f => f.id);
            setSelectedRows(prev => Array.from(new Set([...prev, ...rangeIds])));
        } else if (e.ctrlKey || e.metaKey) {
            setSelectedRows(prev =>
                prev.includes(id)
                    ? prev.filter(rowId => rowId !== id)
                    : [...prev, id]
            );
            setLastClickedIndex(index);
        }
    };


    const handleInputChange = (e) => {
        setEditedValue(e.target.value);
    };

    const handleBlur = async () => {
        const idsToUpdate = selectedRows.length > 1 ? selectedRows : [editCell.id];
        const isZero = (val) => parseFloat(val) === 0 || val === "" || val === null;
        const fieldsToCheck = ["returnVat", "taxesZus", "partner", "accountant", "carRent", "sallary", "other", "refunds"];

        try {
            // 🔸 Перевірка all-zero тільки якщо один запис
            if (idsToUpdate.length === 1) {
                const id = idsToUpdate[0];
                const fundToUpdate = funds.find(f => f.id === id);
                if (fundToUpdate) {
                    const updatedFund = { ...fundToUpdate, [editCell.field]: editedValue.replace(',', '.') };
                    const allZero = fieldsToCheck.every(field => {
                        const value = field === editCell.field ? updatedFund[field] : fundToUpdate[field];
                        return isZero(value);
                    });

                    if (allZero) {
                        const confirmDelete = window.confirm(
                            t("confirmDeleteZeroRecord") || "Усі значення дорівнюють 0. Бажаєте видалити запис?"
                        );
                        if (confirmDelete) {
                            // 👇 ключовий момент — затримка
                            setTimeout(async () => {
                                try {
                                    await axios.delete(`/api/extraFunds/${id}`, authHeaders());
                                    await fetchFunds();
                                } catch (error) {
                                    console.error("Помилка при видаленні:", error);
                                }
                            }, 0);
                        }
                        setEditCell({ id: null, field: null });
                        return;
                    }
                }
            }

            // 🔸 Масове або звичайне оновлення
            await Promise.all(
                idsToUpdate.map(id => {
                    const fundToUpdate = funds.find(f => f.id === id);
                    if (!fundToUpdate) return null;

                    const updatedData = { ...fundToUpdate, [editCell.field]: editedValue.replace(',', '.') };
                    return axios.put(`/api/extraFunds/${id}`, updatedData, authHeaders());
                })
            );

            await fetchFunds();
        } catch (error) {
            console.error("Помилка масового оновлення:", error);
        }

        setEditCell({ id: null, field: null });
    };





    const handleEdit = (id) => {
        navigate(`/admin/edit-extra-funds/${id}`);
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t("deleteQuestion"))) return;

        try {
            await axios.delete(`/api/extraFunds/${id}`, authHeaders());
            fetchFunds();
        } catch (error) {
            console.error("Error deleting extra fund", error);
            alert(t("deleteFalseMessage"));
        }
    };

    const filteredFunds = funds.filter((fund) => {
        const matchDriver = filters.user_id ? String(fund.driver_id) === String(filters.user_id.value) : true;
        const matchWeek = !selectedWeek || String(fund.week_number) === String(selectedWeek);
        return matchDriver && matchWeek;
    });

    const currentItems = filteredFunds.slice(indexOfFirstItem, indexOfLastItem);

    const getDriverOpenDebts = (driverId) =>
        (allDebts || []).filter((d) => String(d.driver_id) === String(driverId) && d.debt_status !== "closed");

    const openDebtsModal = (driverId, e) => {
        if (e) e.stopPropagation();
        setDriverDebtsModalDriverId(driverId);
        setShowDriverDebtsModal(true);
    };

    const driverDebtsModalDriverName = driverDebtsModalDriverId != null
        ? (() => {
            const f = funds.find((x) => String(x.driver_id) === String(driverDebtsModalDriverId));
            return f ? `${f.firstName} ${f.lastName}` : "";
        })()
        : "";
    const driverDebtsList = driverDebtsModalDriverId != null
        ? allDebts
            .filter((d) => String(d.driver_id) === String(driverDebtsModalDriverId) && d.debt_status !== "closed")
            .sort((a, b) => {
                const dateA = a.created_at ? new Date(a.created_at) : new Date(a.year || 0, 0, (a.week_number || 0) * 7);
                const dateB = b.created_at ? new Date(b.created_at) : new Date(b.year || 0, 0, (b.week_number || 0) * 7);
                return dateB - dateA;
            })
        : [];

    const isNonZero = (v) => {
        const n = parseFloat(String(v || "").replace(",", "."));
        return !Number.isNaN(n) && n !== 0;
    };

    const hasNonEmptyDefaults = (row) => {
        if (isNonZero(row.taxesZus) || isNonZero(row.partner) || isNonZero(row.accountant) || isNonZero(row.carRent) || isNonZero(row.sallary)) return true;
        const otherExpenses = Array.isArray(row.otherExpenses) ? row.otherExpenses : [];
        const refundsItems = Array.isArray(row.refundsItems) ? row.refundsItems : [];
        if (otherExpenses.some((e) => (e?.amount ?? "").toString().trim() !== "" || (e?.description ?? "").toString().trim() !== "")) return true;
        if (refundsItems.some((r) => (r?.amount ?? "").toString().trim() !== "" || (r?.description ?? "").toString().trim() !== "")) return true;
        return false;
    };

    const handleCreateForAllWithDefaults = async () => {
        if (!selectedWeek) {
            alert(t("chooseWeek") || t("selectWeek") || "Оберіть тиждень");
            return;
        }
        const config = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
        try {
            const res = await axios.get("/api/extra-funds-default-values", config);
            const allDefaults = res.data || [];
            const withNonEmpty = allDefaults.filter(hasNonEmptyDefaults);
            if (withNonEmpty.length === 0) {
                alert(t("noDriversWithDefaults") || "Немає водіїв з непорожніми стандартними значеннями.");
                return;
            }
            const confirmMsg = t("areYouSureCreateForAllWithDefaults") || `Створити записи для ${withNonEmpty.length} водіїв(я) з непорожніми стандартними значеннями на обраний тиждень?`;
            if (!window.confirm(confirmMsg)) return;

            const total = withNonEmpty.length;
            setCreateForAllTotal(total);
            setCreateForAllCurrent(0);
            setCreateForAllProgress(0);
            setCreateForAllCurrentDriverName("");
            setCreateForAllOverlay(true);

            const driverName = (row) => `${row.firstName || ""} ${row.lastName || ""}`.trim() || String(row.driver_id);

            let created = 0;
            let processed = 0;
            try {
                for (const row of withNonEmpty) {
                    setCreateForAllCurrentDriverName(driverName(row));
                    // Один запис на одного водія за один тиждень — якщо вже є, не створюємо
                    let alreadyExists = false;
                    try {
                        const check = await axios.get("/api/extraFunds/by-driver-week", {
                            params: { driver_id: row.driver_id, week_number: selectedWeek },
                            ...config,
                        });
                        alreadyExists = !!(check.data && check.data.id);
                    } catch (e) {
                        if (e.response?.status !== 404) {
                            processed++;
                            setCreateForAllCurrent(processed);
                            setCreateForAllProgress(Math.round((processed / total) * 100));
                            await new Promise((r) => setTimeout(r, 700));
                            continue;
                        }
                    }
                    if (alreadyExists) {
                        processed++;
                        setCreateForAllCurrent(processed);
                        setCreateForAllProgress(Math.round((processed / total) * 100));
                        await new Promise((r) => setTimeout(r, 700));
                        continue;
                    }

                    const otherExpenses = Array.isArray(row.otherExpenses) ? row.otherExpenses : [];
                    const refundsItems = Array.isArray(row.refundsItems) ? row.refundsItems : [];
                    const totalOther = otherExpenses.reduce((sum, e) => sum + (parseFloat(e?.amount) || 0), 0);
                    const totalRefunds = refundsItems.reduce((sum, r) => sum + (parseFloat(r?.amount) || 0), 0);
                    try {
                        await axios.post(
                            "/api/extraFunds",
                            {
                                driver_id: row.driver_id,
                                week_number: selectedWeek,
                                returnVat: parseFloat(row.returnVat) || 0,
                                taxesZus: parseFloat(row.taxesZus) || 0,
                                partner: parseFloat(row.partner) || 0,
                                accountant: parseFloat(row.accountant) || 0,
                                carRent: parseFloat(row.carRent) || 0,
                                sallary: parseFloat(row.sallary) || 0,
                                other: totalOther,
                                otherExpenses,
                                refunds: totalRefunds,
                                refundsDesc: JSON.stringify(refundsItems),
                            },
                            config
                        );
                        created++;
                    } catch (postErr) {
                        if (postErr.response?.status === 409) { /* skip */ }
                        else throw postErr;
                    }
                    processed++;
                    setCreateForAllCurrent(processed);
                    setCreateForAllProgress(Math.round((processed / total) * 100));
                    await new Promise((r) => setTimeout(r, 700));
                }
                fetchFunds();
            } catch (err) {
                console.error("Create for all (defaults):", err);
                alert(t("errorCreateForAll") || "Помилка при створенні записів.");
            } finally {
                setCreateForAllOverlay(false);
            }
        } catch (err) {
            console.error("Create for all (defaults):", err);
            alert(t("errorCreateForAll") || "Помилка при створенні записів.");
            setCreateForAllOverlay(false);
        }
    };

    return (
        <>
            {createForAllOverlay && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        backgroundColor: "rgba(0,0,0,0.5)",
                        zIndex: 1050,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <div className="bg-white rounded shadow-lg p-4" style={{ minWidth: "320px" }}>
                        <div className="mb-3 fw-bold">
                            {t("extraCosts")} — {t("createForAllUsers")}
                        </div>
                        <div className="mb-2 small text-muted">
                            {createForAllCurrent} / {createForAllTotal}
                        </div>
                        {createForAllCurrentDriverName && (
                            <div className="mb-2 fw-medium text-dark">
                                {createForAllCurrentDriverName}
                            </div>
                        )}
                        <Progress now={createForAllProgress} label={`${createForAllProgress}%`} style={{ height: "24px" }} />
                    </div>
                </div>
            )}
            <Container>
            <h2>{t("extraCosts")}</h2>
            <Form>
                <Row className="g-2 align-items-stretch">
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-0 w-100 d-flex flex-column">
                            <Form.Label className="small mb-1 flex-shrink-0" style={{ minHeight: "20px", lineHeight: "20px" }}>{t("driver")}</Form.Label>
                            <div className="w-100 flex-grow-1" style={{ height: "38px" }}>
                                <DriverSelector
                                    selectedDriver={filters.user_id}
                                    setSelectedDriver={(selectedOption) => setFilters({ ...filters, user_id: selectedOption })}
                                    className="extra-funds-driver-select"
                                />
                            </div>
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-0 w-100 d-flex flex-column">
                            <Form.Label className="small mb-1 flex-shrink-0" style={{ minHeight: "20px", lineHeight: "20px" }}>{t("week")}</Form.Label>
                            <div className="w-100 flex-grow-1" style={{ height: "38px" }}>
                                <WeekSelector
                                    selectedWeek={selectedWeek}
                                    setSelectedWeek={setSelectedWeek}
                                    className="mb-0 w-100"
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
            </Form>
            <Row className="mt-4 g-2">
                <Col xs={12} md={selectedRows.length > 0 ? 3 : 4}>
                    <Button
                        as="a"
                        href="/admin/edit-extra-funds"
                        variant="outline-primary"
                        className="w-100 d-flex align-items-center justify-content-center gap-2"
                    >
                        <AiOutlinePlus size={18} />
                        {t("create")}
                    </Button>
                </Col>
                <Col xs={12} md={selectedRows.length > 0 ? 3 : 4}>
                    <Button
                        variant="outline-primary"
                        className="w-100 d-flex align-items-center justify-content-center gap-2"
                        onClick={handleCreateForAllWithDefaults}
                    >
                        <AiOutlineTeam size={18} />
                        {t("createForAllUsers")}
                    </Button>
                </Col>
                <Col xs={12} md={selectedRows.length > 0 ? 3 : 4}>
                    <Button
                        as="a"
                        href="/admin/extra-funds-default-values"
                        variant="outline-primary"
                        className="w-100 d-flex align-items-center justify-content-center gap-2"
                    >
                        <AiOutlineUnorderedList size={18} />
                        {t("standardValues")}
                    </Button>
                </Col>
                {selectedRows.length > 0 && (
                    <Col xs={12} md={3}>
                        <Button
                            variant="outline-danger"
                            className="w-100 d-flex align-items-center justify-content-center gap-2"
                            onClick={async () => {
                                if (window.confirm(t("confirmdelete"))) {
                                    try {
                                        await Promise.all(selectedRows.map(id =>
                                            axios.delete(`/api/extraFunds/${id}`, authHeaders())
                                        ));
                                        fetchFunds();
                                        setSelectedRows([]);
                                    } catch (error) {
                                        console.error("Error deleting selected:", error);
                                    }
                                }
                            }}
                        >
                            <AiOutlineDelete size={18} />
                            {t("deleteSelected")}
                        </Button>
                    </Col>
                )}
            </Row>



            <h3 className="mt-4">{t("listOf")} {t("extraCosts").toLocaleLowerCase()}</h3>
            <Table striped bordered hover>
                <thead className="sticky-top">
                    <tr>
                        <th>{t("driver")}</th>
                        <th>{t("week")}</th>
                        <th>{t("vat")}</th>
                        <th>{t("zus")}</th>
                        <th>{t("partner")}</th>
                        <th>{t("accountant")}</th>
                        <th>{t("carRent")}</th>
                        <th>{t("sallary")}</th>
                        <th>{t("debtsMulti")}</th>
                        <th>{t("other")}</th>
                        <th>{t("refunds")}</th>
                        <th>{t("actions")}</th>
                    </tr>
                </thead>
                <tbody>
                    {currentItems.map((fund, index) => (
                        <tr
                            key={fund.id}
                            onClick={(e) => handleRowClick(e, index + indexOfFirstItem, fund.id)}
                            className={selectedRows.includes(fund.id) ? "table-primary" : ""}
                        >
                            <td>{fund.firstName} {fund.lastName}</td>
                            <td>{fund.week_number}</td>
                            {["returnVat", "taxesZus", "partner", "accountant", "carRent", "sallary", "other", "refunds"].map(field => (
                                <React.Fragment key={field}>
                                    <td
                                        className="align-middle position-relative"
                                        onDoubleClick={() => {
                                            if (field !== "other" && field !== "refunds") {
                                                handleDoubleClick(fund.id, field, fund[field]);
                                            }
                                        }}
                                    >
                                        {editCell.id === fund.id && editCell.field === field ? (
                                            <>
                                                <span className="invisible text-nowrap" aria-hidden>
                                                    {fund[field] ?? ""}
                                                </span>
                                                <Form.Control
                                                    type="text"
                                                    className="position-absolute top-50 start-0 end-0 translate-middle-y mx-1"
                                                    style={{ width: "auto", maxWidth: "calc(100% - 0.5rem)" }}
                                                    value={editedValue}
                                                    onChange={handleInputChange}
                                                    onBlur={() => {
                                                        if (!skipBlur) handleBlur();
                                                        else setSkipBlur(false); // збити флаг
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            handleBlur();
                                                        }
                                                    }}
                                                    autoFocus
                                                    size="sm"
                                                />
                                            </>
                                        ) : (
                                            fund[field]
                                        )}
                                    </td>
                                    {field === "sallary" && (
                                        <td
                                            className="text-nowrap text-center"
                                            style={{ cursor: "pointer", verticalAlign: "middle" }}
                                            onClick={(e) => openDebtsModal(fund.driver_id, e)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => e.key === "Enter" && openDebtsModal(fund.driver_id, e)}
                                        >
                                            {(() => {
                                                const open = getDriverOpenDebts(fund.driver_id);
                                                const total = open.reduce((sum, d) => sum + (parseFloat(d.debt_total) || 0), 0);
                                                if (total === 0) {
                                                    return (
                                                        <span className="d-inline-flex align-items-center justify-content-center">
                                                            <AiOutlineMinus
                                                                className="text-success"
                                                                size={20}
                                                                title={t("noDebts") || "Боргів немає"}
                                                            />
                                                        </span>
                                                    );
                                                }
                                                return (
                                                    <span className="text-danger fw-bold">
                                                        {total.toFixed(2)}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                    )}
                                </React.Fragment>
                            ))}
                            <td>
                                <AiOutlineEdit
                                    style={{ cursor: "pointer", marginRight: "10px", color: "orange" }}
                                    size={20}
                                    onClick={() => handleEdit(fund.id)}
                                />
                                <AiOutlineDelete
                                    style={{ cursor: "pointer", color: "red" }}
                                    size={20}
                                    onClick={() => handleDelete(fund.id)}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            {filteredFunds.length > itemsPerPage && (
                <Pagination className="justify-content-center mt-4">
                    <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} />

                    {Array.from({ length: Math.ceil(filteredFunds.length / itemsPerPage) }, (_, i) => (
                        <Pagination.Item
                            key={i + 1}
                            active={i + 1 === currentPage}
                            onClick={() => setCurrentPage(i + 1)}
                        >
                            {i + 1}
                        </Pagination.Item>
                    ))}

                    <Pagination.Next
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredFunds.length / itemsPerPage)))}
                        disabled={currentPage === Math.ceil(filteredFunds.length / itemsPerPage)}
                    />
                    <Pagination.Last
                        onClick={() => setCurrentPage(Math.ceil(filteredFunds.length / itemsPerPage))}
                        disabled={currentPage === Math.ceil(filteredFunds.length / itemsPerPage)}
                    />
                </Pagination>
            )}

            {/* Модальне вікно: активні борги водія */}
            <Modal show={showDriverDebtsModal} onHide={() => setShowDriverDebtsModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {t("debtsMulti")} — {driverDebtsModalDriverName}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    {driverDebtsList.length === 0 ? (
                        <p className="p-4 text-muted mb-0">{t("noRecords")}</p>
                    ) : (
                        <ul className="list-group list-group-flush">
                            {driverDebtsList.map((debt) => (
                                <li
                                    key={debt.id}
                                    className="list-group-item d-flex align-items-start justify-content-between gap-3 py-3"
                                    style={{ borderLeft: `3px solid ${debt.debt_status === "closed" ? "var(--bs-secondary)" : "var(--bs-danger)"}` }}
                                >
                                    <div className="d-flex flex-column min-w-0 flex-grow-1">
                                        <Link
                                            to={`/admin/driver-debts/${debt.id}`}
                                            className={`text-break text-decoration-none ${debt.debt_status !== "closed" ? "fw-bold text-danger" : "fw-semibold text-dark"}`}
                                            onClick={() => setShowDriverDebtsModal(false)}
                                        >
                                            {debt.debt_name}
                                        </Link>
                                        {(debt.debt_create_desc || debt.week_number != null) && (
                                            <span className="small text-muted mt-1 text-break">
                                                {debt.week_number != null && `${t("week")} ${debt.week_number}`}
                                                {debt.week_number != null && debt.debt_create_desc && " · "}
                                                {debt.debt_create_desc && (debt.debt_create_desc.length > 80 ? debt.debt_create_desc.slice(0, 80) + "…" : debt.debt_create_desc)}
                                            </span>
                                        )}
                                        {debt.created_at && (
                                            <span className="small text-muted mt-1">
                                                {t("created")}: {new Date(debt.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                                            </span>
                                        )}
                                        {debt.debt_status === "closed" && debt.closed_at && (
                                            <span className="small text-muted mt-1 d-block">
                                                {t("closedAt")}: {new Date(debt.closed_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                                            </span>
                                        )}
                                    </div>
                                    <div className="d-flex align-items-center gap-3 flex-shrink-0">
                                        <span className={debt.debt_status !== "closed" ? "fw-bold text-danger" : "fw-bold"}>
                                            {debt.debt_total} PLN
                                        </span>
                                        <span className={`badge ${debt.debt_status === "closed" ? "bg-secondary" : "bg-danger"}`}>
                                            {debt.debt_status === "closed" ? t("closed") : t("debtStatusOpen")}
                                        </span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDriverDebtsModal(false)}>{t("close")}</Button>
                </Modal.Footer>
            </Modal>

        </Container>
        </>
    );
};

export default ExtraFunds;
